import {
  Autocomplete,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  TextField,
  Tooltip,
} from "@mui/material";
import { Add, Delete, Group, PersonAdd, Send } from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { API_URL } from "const/constants";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import SendAccountGroupToProviderModal from "./SendAccountGroupToProviderModal";

const PICKER_PAGE_SIZE = 50;

const parseAccountsPage = (raw) => {
  if (Array.isArray(raw)) {
    return raw;
  }
  if (Array.isArray(raw?.content)) {
    return raw.content;
  }
  if (Array.isArray(raw?.items)) {
    return raw.items;
  }
  return [];
};

const fetchAccountsForPicker = async (search) => {
  const res = await axios.get(`accounts/aggregated`, {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    params: {
      page: 0,
      size: PICKER_PAGE_SIZE,
      search: search.trim() || undefined,
      sortBy: "updatedDate",
      sortDirection: "DESC",
    },
  });
  return parseAccountsPage(res?.data?.data);
};

const accountLabel = (account) => {
  if (!account) return "Unknown account";
  const passport = account?.clientData?.passportNumber;
  return `${account.clientName || "Unnamed"}${passport ? ` - ${passport}` : ""}`;
};

const uniqueValues = (values) =>
  [
    ...new Set(
      values
        .filter((value) => typeof value === "string" && value.trim())
        .map((value) => value.trim())
    ),
  ].sort();

const accountProviders = (account) => account?.admissionProviders || [];
const accountCourses = (account) => account?.admissionCourses || [];

const matchesProviderFilter = (account, provider) =>
  !provider || accountProviders(account).some((item) => item === provider);

export default function AccountMasterGroupManager({
  open,
  onClose,
  groups = [],
  providerList = [],
  refetchProvider,
  onRefresh,
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [addMemberGroupId, setAddMemberGroupId] = useState(null);
  const [sendGroup, setSendGroup] = useState(null);
  const [accountSearch, setAccountSearch] = useState("");
  const [providerFilter, setProviderFilter] = useState(null);
  const [addMemberSearch, setAddMemberSearch] = useState("");
  const [addMemberProviderFilter, setAddMemberProviderFilter] = useState(null);
  const [groupSearch, setGroupSearch] = useState("");
  const [debouncedAccountSearch, setDebouncedAccountSearch] = useState("");
  const [debouncedAddMemberSearch, setDebouncedAddMemberSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedAccountSearch(accountSearch), 350);
    return () => clearTimeout(timer);
  }, [accountSearch]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedAddMemberSearch(addMemberSearch), 350);
    return () => clearTimeout(timer);
  }, [addMemberSearch]);

  const { data: createPickerAccounts = [], isFetching: isCreatePickerLoading } = useQuery({
    queryKey: ["account-group-picker", "create", debouncedAccountSearch],
    queryFn: () => fetchAccountsForPicker(debouncedAccountSearch),
    enabled: open,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  });

  const { data: addMemberPickerAccounts = [], isFetching: isAddMemberPickerLoading } = useQuery({
    queryKey: ["account-group-picker", "add-member", debouncedAddMemberSearch],
    queryFn: () => fetchAccountsForPicker(debouncedAddMemberSearch),
    enabled: !!addMemberGroupId,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  });

  const groupedAccountIds = useMemo(
    () =>
      new Set(
        groups.flatMap((group) => group.members?.map((member) => member.accountMasterId) || [])
      ),
    [groups]
  );

  const filterUngroupedAccounts = (accounts, provider) =>
    accounts.filter(
      (account) =>
        account?.clientAccountId &&
        !groupedAccountIds.has(account.clientAccountId) &&
        matchesProviderFilter(account, provider)
    );

  const filteredUngroupedAccounts = useMemo(
    () => filterUngroupedAccounts(createPickerAccounts, providerFilter),
    [createPickerAccounts, groupedAccountIds, providerFilter]
  );

  const filteredAddMemberAccounts = useMemo(
    () => filterUngroupedAccounts(addMemberPickerAccounts, addMemberProviderFilter),
    [addMemberPickerAccounts, groupedAccountIds, addMemberProviderFilter]
  );

  const providerOptions = useMemo(
    () =>
      uniqueValues(
        [...createPickerAccounts, ...addMemberPickerAccounts].flatMap((account) =>
          accountProviders(account)
        )
      ),
    [createPickerAccounts, addMemberPickerAccounts]
  );

  const accountById = useMemo(() => {
    const map = new Map();
    [...createPickerAccounts, ...addMemberPickerAccounts].forEach((account) => {
      if (account?.clientAccountId) {
        map.set(account.clientAccountId, account);
      }
    });
    return map;
  }, [createPickerAccounts, addMemberPickerAccounts]);

  const resetCreate = () => {
    setName("");
    setDescription("");
    setSelectedAccounts([]);
    setAccountSearch("");
    setProviderFilter(null);
  };

  const filteredGroups = useMemo(() => {
    if (!groupSearch.trim()) return groups;
    return groups.filter((group) => {
      const memberText = (group.members || [])
        .map((member) => {
          const account = accountById.get(member.accountMasterId);
          return account
            ? [
                accountLabel(account),
                ...accountProviders(account),
                ...accountCourses(account),
              ].join(" ")
            : member.clientName || "";
        })
        .join(" ");
      const text = [group.name, group.description, memberText]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return text.includes(groupSearch.trim().toLowerCase());
    });
  }, [groups, groupSearch, accountById]);

  const toggleAccount = (accountId) => {
    setSelectedAccounts((prev) =>
      prev.includes(accountId) ? prev.filter((id) => id !== accountId) : [...prev, accountId]
    );
  };

  const createGroup = async () => {
    if (!name.trim()) {
      toast.warning("Group name is required");
      return;
    }
    try {
      await axios.post(`${API_URL}/account-master-groups`, {
        name: name.trim(),
        description: description.trim() || null,
        accountMasterIds: selectedAccounts,
      });
      toast.success("Group created");
      resetCreate();
      onRefresh?.();
    } catch (e) {
      toast.error(
        e?.response?.data?.errorMessage || e?.response?.data?.message || "Failed to create group"
      );
    }
  };

  const addMembers = async () => {
    if (!selectedAccounts.length) {
      toast.warning("Select at least one account");
      return;
    }
    try {
      await axios.post(
        `${API_URL}/account-master-groups/${addMemberGroupId}/members`,
        selectedAccounts
      );
      toast.success("Members added");
      setAddMemberGroupId(null);
      setSelectedAccounts([]);
      onRefresh?.();
    } catch (e) {
      toast.error(
        e?.response?.data?.errorMessage || e?.response?.data?.message || "Failed to add members"
      );
    }
  };

  const removeMember = async (groupId, accountMasterId) => {
    try {
      await axios.delete(`${API_URL}/account-master-groups/${groupId}/members`, {
        data: [accountMasterId],
      });
      toast.success("Member removed");
      onRefresh?.();
    } catch (e) {
      toast.error(
        e?.response?.data?.errorMessage || e?.response?.data?.message || "Failed to remove member"
      );
    }
  };

  const deleteGroup = async (groupId) => {
    if (!window.confirm("Delete this account group?")) return;
    try {
      await axios.delete(`${API_URL}/account-master-groups/${groupId}`);
      toast.success("Group deleted");
      onRefresh?.();
    } catch (e) {
      toast.error(
        e?.response?.data?.errorMessage || e?.response?.data?.message || "Failed to delete group"
      );
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Manage Account Groups</DialogTitle>
        <DialogContent>
          <div className="grid grid-cols-1 md:grid-cols-[300px_1fr] gap-4 mt-1">
            <div className="rounded border border-app-border p-3 bg-app-surface-muted">
              <div className="font-semibold text-app-text mb-2">New Group</div>
              <TextField
                label="Group name"
                size="small"
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
                margin="dense"
              />
              <TextField
                label="Description"
                size="small"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                fullWidth
                margin="dense"
              />
              <div className="mt-3">
                <div className="text-xs font-semibold text-app-text-muted mb-1">
                  Ungrouped accounts
                </div>
                <TextField
                  label="Search accounts"
                  size="small"
                  value={accountSearch}
                  onChange={(e) => setAccountSearch(e.target.value)}
                  fullWidth
                  margin="dense"
                />
                <Autocomplete
                  size="small"
                  options={providerOptions}
                  value={providerFilter}
                  onChange={(_, value) => setProviderFilter(value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Filter by uni/college"
                      size="small"
                      margin="dense"
                    />
                  )}
                />
                <div className="flex flex-wrap gap-1 max-h-52 overflow-y-auto">
                  {isCreatePickerLoading && filteredUngroupedAccounts.length === 0 ? (
                    <span className="text-xs text-app-text-muted inline-flex items-center gap-1">
                      <CircularProgress size={14} />
                      Loading accounts…
                    </span>
                  ) : filteredUngroupedAccounts.length === 0 ? (
                    <span className="text-xs text-app-text-muted">
                      No matching ungrouped accounts.
                    </span>
                  ) : (
                    filteredUngroupedAccounts.map((account) => (
                      <Chip
                        key={account.clientAccountId}
                        label={accountLabel(account)}
                        size="small"
                        clickable
                        color={
                          selectedAccounts.includes(account.clientAccountId) ? "primary" : "default"
                        }
                        onClick={() => toggleAccount(account.clientAccountId)}
                        sx={{ fontSize: 11 }}
                      />
                    ))
                  )}
                </div>
              </div>
              <Button
                className="!mt-3"
                variant="contained"
                size="small"
                startIcon={<Add />}
                onClick={createGroup}
                fullWidth
              >
                Create Group
              </Button>
            </div>

            <div className="space-y-3">
              <TextField
                label="Search groups or clients"
                size="small"
                value={groupSearch}
                onChange={(e) => setGroupSearch(e.target.value)}
                fullWidth
              />
              {filteredGroups.length === 0 ? (
                <div className="rounded border border-dashed border-app-border p-4 text-sm text-app-text-muted">
                  No matching account groups.
                </div>
              ) : (
                filteredGroups.map((group) => (
                  <div
                    key={group.id}
                    className="rounded border border-app-border bg-app-surface p-3"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <Group fontSize="small" color="primary" />
                          <span className="font-semibold text-app-text">{group.name}</span>
                          <Chip
                            size="small"
                            label={`${group.members?.length || 0} account${
                              group.members?.length === 1 ? "" : "s"
                            }`}
                          />
                        </div>
                        {group.description && (
                          <div className="text-xs text-app-text-muted mt-1">
                            {group.description}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <Tooltip title="Send group to provider">
                          <IconButton size="small" onClick={() => setSendGroup(group)}>
                            <Send fontSize="small" color="primary" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Add accounts">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setAddMemberGroupId(group.id);
                              setSelectedAccounts([]);
                            }}
                          >
                            <PersonAdd fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete group">
                          <IconButton size="small" onClick={() => deleteGroup(group.id)}>
                            <Delete fontSize="small" color="error" />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </div>
                    <Divider className="!my-2" />
                    <div className="flex flex-wrap gap-1">
                      {(group.members || []).map((member) => {
                        const account = accountById.get(member.accountMasterId);
                        return (
                          <Chip
                            key={member.id}
                            label={
                              account
                                ? accountLabel(account)
                                : member.clientName || `Account #${member.accountMasterId}`
                            }
                            size="small"
                            onDelete={() => removeMember(group.id, member.accountMasterId)}
                            sx={{ fontSize: 11 }}
                          />
                        );
                      })}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!addMemberGroupId}
        onClose={() => setAddMemberGroupId(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Accounts to Group</DialogTitle>
        <DialogContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2 mb-2">
            <TextField
              label="Search accounts"
              size="small"
              value={addMemberSearch}
              onChange={(e) => setAddMemberSearch(e.target.value)}
              fullWidth
            />
            <Autocomplete
              size="small"
              options={providerOptions}
              value={addMemberProviderFilter}
              onChange={(_, value) => setAddMemberProviderFilter(value)}
              renderInput={(params) => (
                <TextField {...params} label="Filter by uni/college" size="small" />
              )}
            />
          </div>
          <div className="flex flex-wrap gap-1 mt-2">
            {isAddMemberPickerLoading && filteredAddMemberAccounts.length === 0 ? (
              <span className="text-sm text-app-text-muted inline-flex items-center gap-1">
                <CircularProgress size={16} />
                Loading accounts…
              </span>
            ) : filteredAddMemberAccounts.length === 0 ? (
              <span className="text-sm text-app-text-muted">No matching ungrouped accounts.</span>
            ) : (
              filteredAddMemberAccounts.map((account) => (
                <Chip
                  key={account.clientAccountId}
                  label={accountLabel(account)}
                  size="small"
                  clickable
                  color={selectedAccounts.includes(account.clientAccountId) ? "primary" : "default"}
                  onClick={() => toggleAccount(account.clientAccountId)}
                  sx={{ fontSize: 11 }}
                />
              ))
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddMemberGroupId(null)}>Cancel</Button>
          <Button variant="contained" onClick={addMembers}>
            Add Selected
          </Button>
        </DialogActions>
      </Dialog>

      <SendAccountGroupToProviderModal
        open={!!sendGroup}
        onClose={() => setSendGroup(null)}
        group={sendGroup}
        providerList={providerList}
        refetchProvider={refetchProvider}
        onSent={onRefresh}
      />
    </>
  );
}
